// Планировщик раунда (стадия 1): скоординированные атаки группами.
//
// Отличие от кампаний (campaigns.js): кампания назначает НА цель ОДИН отряд, и
// атаки идут поодиночке — отряды гибнут о гарнизон по частям. Планировщик
// собирает под цель ГРУППУ с силой, достаточной против гарнизона с запасом, и
// синхронизирует подход: дальние выступают сразу, ближние подключаются, только
// когда группа выравнивается по времени прибытия (план пересобирается каждый
// раунд, поэтому ближние присоединяются сами, когда дальние подтянулись).
//
// Все цели сравниваются в единой валюте — ожидаемых очках к концу игры:
// захват города = двойное качание (+ценность себе, −владельцу) плюс поток
// дохода за оставшиеся раунды, дисконт за время подхода.
import {calcBfsDistances} from "../botGrid";
import {getCachedRoundPlan, setCachedRoundPlan} from "./plannerStore";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {calcCityWinPoints} from "../../gameState/winPoints/calcCityWinPoints";
import {calcCityGarrisonStrength, findOwnCitiesUnderThreat} from "../evaluation/armyStrength";
import {getKeyPointValuePerRound} from "../evaluation/keyPoints";
import {selectRuinsIncomeRoundLimit} from "../../gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {getUnitTypeConfig} from "../../../configs/units";
import {KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {FINAL_ROUND} from "../../../configs/game";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";

// Проба 4 (300 партий): группы атак в чистом виде — НЕТТО-МИНУС (−3.7 п.п.
// при большем числе захватов): марширующие отряды не защищают экономику, а
// доход с активов — главная валюта победы (см. new_arch_plan.md). До пивота
// на роллаут-выбор планов форс-штурмы включаются только против ПУСТЫХ или
// почти пустых целей рядом (дешевые захваты без отрыва от базы).
const MAX_GARRISON_FOR_CHEAP_CAPTURE = 2;

// Дальше этого расстояния (BFS от ближайшего отряда) цель не рассматривается
const MAX_PLAN_DISTANCE = 6;
// Запас силы группы против гарнизона: сила >= гарнизон * K + BASE
const FORCE_RATIO = 1.25;
const FORCE_BASE = 3;
// Прогнозный рост гарнизона за раунд подхода (снабжение/подкрепления)
const GARRISON_GROWTH_PER_ROUND = 0.12;
// Отряды меньше этого размера в атакующие группы не берутся
const MIN_ASSAULT_UNIT_SIZE = 3;
// Максимальный разброс времени прибытия внутри группы, при котором ближним
// уже назначается цель (иначе они ждут, продолжая обычное поведение)
const MAX_ETA_SPREAD = 1;

const KEY_POINT_TOKEN_VALUES = Object.values(KEY_POINT_TOKENS);

/**
 * Поток дохода города в очках за раунд: ключевые точки по их номиналу,
 * гильдии/храмы грубо по 1.5 (доход + типичный путь влияния).
 */
const calcCityIncomePerRound = (city, round, ruinsIncomeRoundLimit) => {
  let income = 0;
  KEY_POINT_TOKEN_VALUES.forEach((token) => {
    (city[token] || []).forEach(() => {
      income += getKeyPointValuePerRound(token, round, ruinsIncomeRoundLimit);
    });
  });
  income += ((city[SPECIAL_BUILDING_TOKENS.GUILD] || []).length
    + (city[SPECIAL_BUILDING_TOKENS.TEMPLE] || []).length) * 1.5;
  return income;
};

/**
 * План раунда: атакующие группы.
 * options задает агрессивность (для роллаут-кандидатов):
 *   maxGarrison — потолок гарнизона цели (-1 = штурмов нет вовсе);
 *   maxDistance — радиус целей от отрядов пула.
 * Возвращает {assaultTargets: Map<unitId, hexKey>, plannedCities: [{cityKey...}]}.
 */
export const buildRoundPlan = ({
                                 currentPlayer,
                                 units,
                                 players,
                                 citiesCache,
                                 hexes,
                                 gridRenderer,
                                 round,
                                 state,
                               }, options = {}) => {
  // Окно дохода руин — от лимита раундов партии; без состояния (тесты) —
  // прежняя оценка по FINAL_ROUND (значение по умолчанию в keyPoints)
  const ruinsIncomeRoundLimit = state ? selectRuinsIncomeRoundLimit(state) : undefined;
  const maxGarrison = options.maxGarrison ?? MAX_GARRISON_FOR_CHEAP_CAPTURE;
  const maxDistance = options.maxDistance ?? MAX_PLAN_DISTANCE;
  // Фокус-атака: рассматривается ТОЛЬКО город с этим центральным кварталом —
  // вся собираемая сила идет на одну цель, а не размазывается по всем
  const focusCityKey = options.focusCityKey || null;
  const hireFreeze = Boolean(options.hireFreeze);
  // Фокус стройки раунда ('economy' | 'military' | null) — прокидывается в
  // план как есть, применяется скорингом planConstructionChain
  const buildFocus = options.buildFocus || null;
  const assaultTargets = new Map();
  const plannedCities = [];
  const myUnits = units.filter((unit) => unit.ownerId === currentPlayer.id);
  if (myUnits.length === 0) {
    return {assaultTargets, plannedCities, hireFreeze, buildFocus};
  }

  // 1. Резерв обороны: города под угрозой с дефицитом гарнизона забирают
  // ближайшие отряды из атакующего пула (существующий механизм defense-кампаний
  // не может перехватывать форсированные цели, поэтому резервируем здесь)
  const reservedUnitIds = new Set();
  findOwnCitiesUnderThreat(currentPlayer, units, citiesCache, gridRenderer).forEach((threat) => {
    let deficit = threat.enemyStrengthNear - threat.garrison;
    if (deficit <= 0) {
      return;
    }
    const centerDistances = calcBfsDistances([threat.city.centralQuarter], hexes, gridRenderer);
    myUnits
      .filter((unit) => !reservedUnitIds.has(unit.id))
      .map((unit) => ({unit, distance: centerDistances.get(unit.hexKey) ?? Infinity}))
      .sort((a, b) => a.distance - b.distance)
      .forEach(({unit, distance}) => {
        if (deficit <= 0 || distance > 6) {
          return;
        }
        reservedUnitIds.add(unit.id);
        deficit -= unit.size;
      });
  });

  // 2. Атакующий пул и дистанции от каждого отряда
  const poolUnits = myUnits.filter(
    (unit) => !reservedUnitIds.has(unit.id) && unit.size >= MIN_ASSAULT_UNIT_SIZE
  );
  if (poolUnits.length === 0) {
    return {assaultTargets, plannedCities, hireFreeze, buildFocus};
  }
  // Дистанции от отрядов одинаковы для всех роллаут-кандидатов раунда —
  // считаются один раз и переиспользуются через options.sharedUnitDistances
  const unitDistances = options.sharedUnitDistances || new Map(); // unitId -> Map<hexKey, dist>
  poolUnits.forEach((unit) => {
    if (!unitDistances.has(unit.id)) {
      unitDistances.set(unit.id, calcBfsDistances([unit.hexKey], hexes, gridRenderer));
    }
  });
  const unitSpeed = (unit) => Math.max(1, getUnitTypeConfig(unit.type)?.speed || 2);

  // 3. Кандидаты-цели: города других цветов с EV в очках
  const candidates = [];
  Object.entries(citiesCache || {}).forEach(([color, cities]) => {
    if (color === currentPlayer.color) {
      return;
    }
    const owner = players.find((player) => player.color === color);
    const ownerId = owner ? owner.id : NEUTRAL_PLAYER_ID;
    const isNeutral = color === NEUTRAL_PLAYER_COLOR;

    (cities || []).forEach((city) => {
      if (focusCityKey && city.centralQuarter !== focusCityKey) {
        return;
      }
      const quarters = getAllQuartersOfCity(city);
      if (quarters.length === 0) {
        return;
      }
      const garrison = calcCityGarrisonStrength(city, ownerId, units, gridRenderer);
      // Проба 4 показала: дальние силовые штурмы — нетто-минус (марш не
      // прикрывает экономику), поэтому дефолт — только дешевые захваты.
      // Роллауты передают сюда и более агрессивные варианты для сравнения.
      if (maxGarrison < 0 || garrison > maxGarrison) {
        return;
      }
      // Ближайшая четверть по каждому отряду пула: ETA группы считается позже
      const perUnit = poolUnits.map((unit) => {
        const distances = unitDistances.get(unit.id);
        let best = Infinity;
        let bestQuarter = null;
        quarters.forEach((quarterHexKey) => {
          const distance = distances.get(quarterHexKey);
          if (distance !== undefined && distance < best) {
            best = distance;
            bestQuarter = quarterHexKey;
          }
        });
        return {unit, distance: best, quarter: bestQuarter, eta: Math.ceil(best / unitSpeed(unit))};
      }).filter((entry) => entry.distance <= maxDistance);
      if (perUnit.length === 0) {
        return;
      }

      // Качание очков: у живого противника захват отнимает столько же, сколько дает
      const captureSwing = calcCityWinPoints(city) * (isNeutral ? 1 : 2);
      const incomePerRound = calcCityIncomePerRound(city, round, ruinsIncomeRoundLimit);
      // Крепость поглощает 2 из каждых 3 урона (10 прочности) — без большого
      // перевеса штурм таких городов не планируется
      const fortressExtraForce = (city[SPECIAL_BUILDING_TOKENS.FORTRESS] || []).length * 12;
      candidates.push({
        city, color, ownerId, isNeutral, quarters, garrison, perUnit,
        captureSwing, incomePerRound, fortressExtraForce,
      });
    });
  });

  // 4. Жадный выбор целей по дисконтированному EV, сбор групп
  const takenUnitIds = new Set();
  const scoreCandidate = (candidate) => {
    // Группа: ближайшие отряды, пока сила не покроет гарнизон с запасом и ростом
    const available = candidate.perUnit
      .filter(({unit}) => !takenUnitIds.has(unit.id))
      .sort((a, b) => a.eta - b.eta);
    if (available.length === 0) {
      return null;
    }
    const group = [];
    let force = 0;
    let eta = 0;
    for (const entry of available) {
      group.push(entry);
      force += entry.unit.size;
      eta = Math.max(eta, entry.eta);
      const needed = candidate.garrison * (1 + GARRISON_GROWTH_PER_ROUND * eta) * FORCE_RATIO
        + (candidate.garrison > 0 ? FORCE_BASE : 0)
        + candidate.fortressExtraForce;
      if (force >= needed) {
        const remaining = Math.max(0, FINAL_ROUND - round - eta);
        const value = candidate.captureSwing + candidate.incomePerRound * remaining;
        return {group, force, eta, ev: value / (1 + eta)};
      }
    }
    return null; // сил не хватает даже всем пулом
  };

  // Итеративно: на каждом шаге берем лучшую по EV достижимую цель
  for (; ;) {
    let best = null;
    candidates.forEach((candidate) => {
      if (candidate.planned) {
        return;
      }
      const scored = scoreCandidate(candidate);
      if (scored && (!best || scored.ev > best.scored.ev)) {
        best = {candidate, scored};
      }
    });
    if (!best || best.scored.ev <= 0) {
      break;
    }
    const {candidate, scored} = best;
    candidate.planned = true;

    // Синхронизация подхода: дальние выступают сразу; ближние получают цель,
    // только когда их время прибытия отстает от самого дальнего не более чем
    // на MAX_ETA_SPREAD (до тех пор они ведут себя обычно и не лезут в бой
    // поодиночке). Цели раздаются по разным кварталам города — захват требует
    // очистить кварталы от противника и стоять хотя бы на одном.
    const maxEta = scored.eta;
    let quarterIndex = 0;
    scored.group.forEach((entry) => {
      takenUnitIds.add(entry.unit.id);
      if (maxEta - entry.eta > MAX_ETA_SPREAD) {
        return;
      }
      const targetQuarter = candidate.quarters[quarterIndex % candidate.quarters.length];
      quarterIndex += 1;
      assaultTargets.set(entry.unit.id, targetQuarter);
    });
    plannedCities.push({
      centralQuarter: candidate.city.centralQuarter,
      color: candidate.color,
      force: scored.force,
      garrison: candidate.garrison,
      eta: scored.eta,
      ev: scored.ev,
    });
  }

  // Проба 150 (стадия 1+) показала: раздача свободного остатка пула на
  // «ближайшие чужие кварталы» в эндшпиле и на блокировку точек лидера дает
  // РЕГРЕСС (одиночные отряды гибнут по частям — ровно та болезнь, от которой
  // планировщик лечит). Свободные отряды остаются в обычном поведении движка.

  return {assaultTargets, plannedCities, hireFreeze, buildFocus};
};

/**
 * План раунда с кэшем: строится один раз за раунд на игрока (шаги фаз
 * вызываются десятки раз за раунд, пересчет на каждом шаге не нужен).
 */
export const getOrBuildRoundPlan = (deps) => {
  const {currentPlayer, round} = deps;
  const cached = getCachedRoundPlan(currentPlayer.id, round);
  if (cached) {
    return cached;
  }
  const plan = buildRoundPlan(deps);
  setCachedRoundPlan(currentPlayer.id, round, plan);
  return plan;
};
